import React from 'react'
import {
  CheckIcon,
  CrossIcon2,
  ReturnBoxIcon,
  Base,
  BoxEyeIcon
} from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { ProductsTab } from '.'
import InformationIcon from '@/assets/images/icons/InformationIcon.svg'
import { RestockItem } from '@/domain/restock'
import { ReturnedItem, ReturnProcess } from '@/domain/returns'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import clsx from 'clsx'
import { NewReview } from '@/domain/review'
import { PbLogisticsSummaryReturnMethodEnum } from '@itsrever/rever-api'

const { Card } = Base

interface TabsProps {
  processDetails?: ReturnProcess
  restockMode: boolean
  reviewMode: boolean
  setReviewMode: (value: boolean) => void
  returnedItems: ReturnedItem[]
  notReceivedItems: ReturnedItem[]
  reviews: NewReview[]
  setReviews: (reviews: NewReview[]) => void
  restocks: RestockItem[]
  setRestocks: (restocks: RestockItem[]) => void
  hasReviews: boolean
  showRefundIsland: boolean
  isClaim: boolean
  returnMethod?: PbLogisticsSummaryReturnMethodEnum
}

const ProcessIsland: React.FC<TabsProps> = ({
  processDetails,
  restockMode,
  reviewMode,
  setReviewMode,
  returnedItems,
  notReceivedItems,
  reviews,
  setReviews,
  restocks,
  setRestocks,
  hasReviews,
  showRefundIsland,
  isClaim,
  returnMethod
}) => {
  const { t } = useTranslation()
  const isKeepYourItem = processDetails?.isKeepYourItem ?? false

  const role = useAppSelector(
    (store) => store.userApi.getMe.response.user?.role
  )
  const authorizationRequired = processDetails?.authorizationPending ?? false

  const isUserAuthorizedToReview =
    role === Roles.ReadWrite || role === Roles.Reviewer

  const showReviewButton =
    !reviewMode &&
    !restockMode &&
    !hasReviews &&
    returnedItems.length > 0 &&
    isUserAuthorizedToReview &&
    !isKeepYourItem
  const unauthorized = processDetails?.unauthorized ?? false
  const isCanceled = processDetails?.isCanceled ?? false

  const showReviewSection =
    showReviewButton &&
    !authorizationRequired &&
    !unauthorized &&
    !isCanceled &&
    !isClaim

  return (
    <Card
      className="relative h-fit p-5"
      style={{
        borderColor: processDetails?.authorizationPending ? '#facc15' : ''
      }}
    >
      {processDetails?.authorizationPending && (
        <div className="absolute inset-0 rounded-lg bg-white/25"></div>
      )}
      <div className="flex items-center justify-between pb-5">
        <div className="flex items-center">
          {!reviewMode ? (
            <div className="flex items-center gap-2">
              <ReturnBoxIcon className="size-4 text-primary-main" />
              <p className="text-sm font-semibold">
                {t('summary.items_title')}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <BoxEyeIcon className="size-5 text-primary-main" />
              <p className="text-sm font-semibold">
                {t('process_details_page.review_all_title')}
              </p>
            </div>
          )}
        </div>
        {isKeepYourItem ? (
          <div className="flex items-center gap-2 rounded-md border border-solid border-orange-300 bg-orange-100 p-1">
            <img className="size-4" src={InformationIcon} alt="" />
            <p className="semibold text-sm">
              {t('process_details_page.kept_items')}
            </p>
          </div>
        ) : null}
        {showReviewSection ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReviewMode(true)}
              className={clsx(
                'flex items-center justify-between gap-1 rounded-xl px-5 py-2.5 shadow-sm transition-all hover:shadow-md',
                ' border border-solid border-slate-200 bg-gradient-to-t from-slate-50/50 to-white'
              )}
            >
              <p className="text-sm font-medium text-neutral-800">
                {t('process_details_page.approve_decline_button')}
              </p>
              <CheckIcon className="size-6 text-green-500" />
              <CrossIcon2 className="mr-1 size-3 text-red-500" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="flex w-full grow flex-col overflow-y-scroll rounded-b-md bg-white [&::-webkit-scrollbar]:hidden">
        <ProductsTab
          processDetails={processDetails}
          restockMode={restockMode}
          reviewMode={reviewMode}
          setReviewMode={setReviewMode}
          returnedItems={returnedItems}
          notReceivedItems={notReceivedItems}
          reviews={reviews}
          setReviews={setReviews}
          restocks={restocks}
          setRestocks={setRestocks}
          showRefundIsland={showRefundIsland}
          returnMethod={returnMethod}
        />
      </div>
    </Card>
  )
}

export default ProcessIsland
